/**
 * The Survivor Game tab (S1 to S7): a round strip, an entry switcher when
 * the fan holds more than one entry, an Add another entry button while the
 * contest allows another (ISSUE-1043), the skip row and its confirm dialog
 * (ISSUE-1044), My picks, Matches, the status bar and the spread consent
 * dialog. One column at every width, the same content
 * widened (§5.7 decision 6): no responsive breakpoint variant class anywhere
 * in this file.
 */
import { Button, Select, Skeleton, Tabs } from "@sportsmet/ui";
import type { JSX } from "react";

import { NEW_CONTEST_START_ACTION_LABEL } from "../newContest/newContestModel";
import {
  PICK_ENTRY_SWITCHER_LABEL,
  PICK_FAILED_MESSAGE,
  PICK_LOADING_LABEL,
  PICK_RETRY_LABEL,
} from "../pick/pickCopy";
import { SpreadConsentDialog } from "../pick/SpreadConsentDialog";
import { MatchCard } from "./MatchCard";
import { MyPicksList } from "./MyPicksList";
import { RoundStrip } from "./RoundStrip";
import { SkipRoundDialog } from "./SkipRoundDialog";
import {
  SURVIVOR_ADD_ENTRY_LABEL,
  SURVIVOR_ADDING_ENTRY_LABEL,
  SURVIVOR_CONTEST_MENU_LABEL,
  SURVIVOR_CONTEST_MENU_PLACEHOLDER,
  SURVIVOR_MATCHES_HEADING,
} from "./survivorGameCopy";
import { SurvivorSkipRow } from "./SurvivorSkipRow";
import { SurvivorStatusBar } from "./SurvivorStatusBar";
import { useSurvivorGameTab } from "./useSurvivorGameTab";

export type typeSurvivorGameTabProps = {
  poolId: string;
  /**
   * Present only for the Commissioner and Admins. Opens the New contest
   * dialog from the no-contest state (ISSUE-1042 R4).
   */
  onStartContest?: (() => void) | undefined;
};

export function SurvivorGameTab(props: typeSurvivorGameTabProps): JSX.Element {
  const model = useSurvivorGameTab({ poolId: props.poolId });
  const { data, selection } = model;

  return (
    <>
      <div data-testid="survivor-game" className="flex flex-col gap-4">
        <div className="flex flex-col gap-3">
          {data.contestOptions.length > 0 && (
            <div data-testid="survivor-contest-menu">
              <Select
                label={SURVIVOR_CONTEST_MENU_LABEL}
                items={data.contestOptions}
                value={data.contestMenuValue}
                placeholder={SURVIVOR_CONTEST_MENU_PLACEHOLDER}
                name="survivor-contest"
                onValueChange={(contestId) => {
                  void model.selectContest(contestId);
                }}
              />
            </div>
          )}
          {data.entryOptions.length > 1 && (
            <Tabs
              label={PICK_ENTRY_SWITCHER_LABEL}
              value={data.selectedEntryId}
              items={data.entryOptions.map((option) => ({
                value: option.entryId,
                label: option.label,
                content: null,
              }))}
              onValueChange={(entryId) => {
                void model.selectEntry(entryId);
              }}
            />
          )}
          {model.addEntryAction !== null && (
            <Button
              variant="secondary"
              className="self-start"
              data-testid="survivor-add-entry"
              loading={model.addEntryAction.isPending}
              loadingLabel={SURVIVOR_ADDING_ENTRY_LABEL}
              onClick={model.addEntryAction.onPress}
            >
              {SURVIVOR_ADD_ENTRY_LABEL}
            </Button>
          )}
          <RoundStrip
            chips={data.roundChips}
            selectedRoundId={data.selectedRoundId}
            onSelectRound={(roundId) => {
              void model.selectRound(roundId);
            }}
          />
          {data.view.kind === "loading" && (
            <div
              data-testid="survivor-game-loading"
              className="flex flex-col gap-3"
            >
              <Skeleton shape="card" label={PICK_LOADING_LABEL} />
              <Skeleton shape="card" label={PICK_LOADING_LABEL} />
              <Skeleton shape="card" label={PICK_LOADING_LABEL} />
            </div>
          )}
          {data.view.kind === "failed" && (
            <div
              data-testid="survivor-game-failed"
              className="flex flex-col gap-2"
            >
              <p className="text-body text-ink">{PICK_FAILED_MESSAGE}</p>
              <Button variant="secondary" onClick={data.retry}>
                {PICK_RETRY_LABEL}
              </Button>
            </div>
          )}
          {"note" in data.view && (
            <p data-testid="survivor-phase-note" className="text-body text-ink">
              {data.view.note}
            </p>
          )}
          {/* Once any contest is scheduled the note says when it starts
              (ISSUE-1042 R3) and the Contests card makes the next one. */}
          {props.onStartContest !== undefined &&
            data.view.kind === "noContest" &&
            !data.hasScheduledContest && (
              <Button
                variant="primary"
                onClick={props.onStartContest}
                data-testid="survivor-start-contest"
              >
                {NEW_CONTEST_START_ACTION_LABEL}
              </Button>
            )}
          {selection.inlineNote !== null && (
            <p
              data-testid="survivor-game-inline-note"
              className="text-body text-ink-secondary"
            >
              {selection.inlineNote}
            </p>
          )}
          {model.skip.note !== null && (
            <p
              data-testid="survivor-skip-note"
              className="text-body text-ink-secondary"
            >
              {model.skip.note}
            </p>
          )}
          {model.skip.row !== null && (
            <SurvivorSkipRow
              view={model.skip.row}
              isPending={model.skip.isPending}
              onSkip={model.skip.openDialog}
              onUndo={model.skip.undoSkip}
            />
          )}
        </div>
        <MyPicksList
          cards={model.myPickCards}
          showPriority={model.showPriority}
          disabled={model.isReadOnly}
          onMove={selection.movePick}
          onRemove={selection.removePick}
        />
        {data.boardData !== null && (
          <section
            data-testid="survivor-matches"
            className="flex flex-col gap-3"
          >
            <h2 className="text-body-strong text-ink">
              {SURVIVOR_MATCHES_HEADING}
            </h2>
            {model.matchCards.map((card) => (
              <MatchCard
                key={card.tile.eventId}
                tile={card.tile}
                gameStateLabel={card.gameStateLabel}
                homeLogoUrl={card.homeLogoUrl}
                awayLogoUrl={card.awayLogoUrl}
                tone={card.tone}
                toneLabel={card.toneLabel}
                priority={card.priority}
                disabled={model.isReadOnly}
                onSelectSide={selection.selectSide}
                onChangePriority={selection.movePick}
              />
            ))}
          </section>
        )}
        {model.statusBar !== null && (
          <div
            data-testid="survivor-status-dock"
            className="sm-sticky-above-nav sm-docked-action sticky bottom-0 z-10"
          >
            <SurvivorStatusBar {...model.statusBar} />
          </div>
        )}
      </div>
      <SpreadConsentDialog
        open={selection.pendingConsent !== null}
        teamName={selection.pendingConsent?.teamName ?? ""}
        onConfirm={selection.confirmConsent}
        onCancel={selection.cancelConsent}
      />
      <SkipRoundDialog
        open={model.skip.isDialogOpen}
        body={model.skip.dialogBody}
        isPending={model.skip.isPending}
        onConfirm={model.skip.confirmSkip}
        onCancel={model.skip.cancelDialog}
      />
    </>
  );
}
